//
// Copyright 2023 DXOS.org
//

import { create } from '@bufbuild/protobuf';
import { EmptySchema } from '@bufbuild/protobuf/wkt';

import { TimeoutError as AsyncTimeoutError, asyncTimeout, scheduleTaskInterval } from '@dxos/async';
import { Context } from '@dxos/context';
import { invariant } from '@dxos/invariant';
import { type PublicKey } from '@dxos/keys';
import { log } from '@dxos/log';
import { RpcClosedError } from '@dxos/protocols';
import { fromDate, toDate } from '@dxos/protocols/buf';
import { type BufService, getBufService } from '@dxos/protocols/buf-service';
import {
  ControlHeartbeatRequestSchema,
  ControlHeartbeatResponseSchema,
  ControlService as ControlServiceDesc,
  RegisterExtensionRequestSchema,
} from '@dxos/protocols/buf/dxos/mesh/teleport/control_pb';
import { type ProtoRpcPeer, createProtoRpcPeer } from '@dxos/rpc';
import { Callback } from '@dxos/util';

import { type ExtensionContext, type TeleportExtension } from './teleport.ts';

const HEARTBEAT_RTT_WARN_THRESH = 10_000;
const DEBUG_PRINT_HEARTBEAT = false; // very noisy

type ControlService = BufService<typeof ControlServiceDesc>;

type ControlRpcBundle = {
  Control: ControlService;
};

type ControlExtensionOpts = {
  heartbeatInterval: number;
  heartbeatTimeout: number;
  onTimeout: (err: any) => void;
};

export class ControlExtension implements TeleportExtension {
  private readonly _ctx = new Context({
    onError: (err) => {
      this._extensionContext?.close(err);
    },
  });

  public readonly onExtensionRegistered = new Callback<(extensionName: string) => void>();

  // Assigned in `onOpen`; both remain undefined if the extension is aborted or closed before it opens.
  private _extensionContext?: ExtensionContext;
  private _rpc?: ProtoRpcPeer<{ Control: ControlService }>;

  constructor(
    private readonly opts: ControlExtensionOpts,
    private readonly localPeerId: PublicKey,
    private readonly remotePeerId: PublicKey,
  ) {}

  /**
   * Announces a local extension to the remote peer.
   * @throws If called before `onOpen` has initialized the control RPC.
   */
  async registerExtension(name: string): Promise<void> {
    invariant(this._rpc, 'Control extension not open.');
    await this._rpc.rpc.Control.registerExtension(create(RegisterExtensionRequestSchema, { name }));
  }

  async onOpen(extensionContext: ExtensionContext): Promise<void> {
    this._extensionContext = extensionContext;

    const rpc = createProtoRpcPeer<ControlRpcBundle, ControlRpcBundle>({
      requested: {
        Control: getBufService<ControlService>('dxos.mesh.teleport.control.ControlService'),
      },
      exposed: {
        Control: getBufService<ControlService>('dxos.mesh.teleport.control.ControlService'),
      },
      handlers: {
        Control: {
          registerExtension: async (request) => {
            this.onExtensionRegistered.call(request.name);
            return create(EmptySchema, {});
          },
          heartbeat: async (request) => {
            if (DEBUG_PRINT_HEARTBEAT) {
              log('received heartbeat request', {
                ts: request.requestTimestamp,
                localPeerId: this.localPeerId.truncate(),
                remotePeerId: this.remotePeerId.truncate(),
              });
            }
            return create(ControlHeartbeatResponseSchema, {
              requestTimestamp: request.requestTimestamp,
            });
          },
        },
      },
      port: await extensionContext.createPort('rpc', {
        contentType: 'application/x-protobuf; messageType="dxos.rpc.Message"',
      }),
      timeout: this.opts.heartbeatTimeout,
    });
    this._rpc = rpc;

    await rpc.open();

    scheduleTaskInterval(
      this._ctx,
      async () => {
        const reqTS = new Date();
        try {
          const resp = await asyncTimeout(
            rpc.rpc.Control.heartbeat(create(ControlHeartbeatRequestSchema, { requestTimestamp: fromDate(reqTS) })),
            this.opts.heartbeatTimeout,
          );
          const now = Date.now();
          // TODO(nf): properly instrument
          const respTimestamp = toDate(resp.requestTimestamp);
          if (respTimestamp) {
            if (
              now - respTimestamp.getTime() >
              (HEARTBEAT_RTT_WARN_THRESH < this.opts.heartbeatTimeout
                ? HEARTBEAT_RTT_WARN_THRESH
                : this.opts.heartbeatTimeout / 2)
            ) {
              log.warn(`heartbeat RTT for Teleport > ${HEARTBEAT_RTT_WARN_THRESH / 1000}s`, {
                rtt: now - respTimestamp.getTime(),
                localPeerId: this.localPeerId.truncate(),
                remotePeerId: this.remotePeerId.truncate(),
              });
            } else {
              if (DEBUG_PRINT_HEARTBEAT) {
                log('heartbeat RTT', {
                  rtt: now - respTimestamp.getTime(),
                  localPeerId: this.localPeerId.truncate(),
                  remotePeerId: this.remotePeerId.truncate(),
                });
              }
            }
          }
        } catch (err: any) {
          const now = Date.now();
          if (err instanceof RpcClosedError) {
            // TODO: expose 'closed' event in Rpc peer to close context as soon the the peer gets closed
            log('ignoring RpcClosedError in heartbeat');
            this._extensionContext?.close(err);
            return;
          }
          if (err instanceof AsyncTimeoutError) {
            log('timeout waiting for heartbeat response', { err, delay: now - reqTS.getTime() });
            this.opts.onTimeout(err);
          } else {
            log.info('other error waiting for heartbeat response', { err, delay: now - reqTS.getTime() });
            this.opts.onTimeout(err);
          }
        }
      },
      this.opts.heartbeatInterval,
    );
  }

  async onClose(err?: Error): Promise<void> {
    await this._ctx.dispose();
    await this._rpc?.close();
  }

  async onAbort(err?: Error | undefined): Promise<void> {
    await this._ctx.dispose();
    await this._rpc?.abort();
  }
}
